import { useIntl } from "react-intl";
import { CourseMaterial } from "./coursesLoader.ts";
import useVirtualStore from "../../common/virtual-store/useVirtualStore.tsx";
import useOrderInfo from "./useOrderInfo.tsx";
import useOptedIn from "./useOptedIn.tsx";
import useTermOptDeadline from "./useTermOptDeadline.tsx";
import { BaseAction, Methods } from "./useMaterialActions.tsx";

export type OrderButtonLinkType =
  | "track_shipment"
  | "campus_pickup_info"
  | "view_order_history"
  | "return_instructions";

export default function useOrderActions({
  courseMaterial,
  methods,
}: {
  courseMaterial: CourseMaterial;
  methods: Methods;
}) {
  const actions: BaseAction[] = [];
  const { orderStatus } = useOrderInfo(courseMaterial);
  const { optWindowOpen } = useTermOptDeadline();
  const isOptedIn = useOptedIn(courseMaterial);
  const intl = useIntl();
  const { isVirtualStore } = useVirtualStore();

  if (!isVirtualStore) {
    return actions;
  }

  const trackShipmentAction = (): BaseAction => {
    const { id, trackingUrl } = courseMaterial?.orderInfo ?? {};

    const actionData = {
      key: "track_shipment",
      label: intl.formatMessage({ defaultMessage: "Track Shipment" }),
      category: "details",
      type: "track_shipment",
    } as BaseAction;

    if (trackingUrl) {
      actionData.onClick = () => {
        window.open(trackingUrl, "_blank", "noopener");
      };
    } else {
      actionData.to = id ? `/order-history/TA-${id}` : "/order-history";
    }

    return actionData;
  };

  const viewOrderHistoryAction = (): BaseAction => ({
    key: "view_order_history",
    label: intl.formatMessage({ defaultMessage: "View Order History" }),
    category: "details",
    to: courseMaterial?.orderInfo?.id
      ? `/order-history/TA-${courseMaterial.orderInfo.id}`
      : "/order-history",
    type: "view_order_history",
  });

  const campusPickupInfoAction = (): BaseAction => ({
    key: "campus_pickup_info",
    label: intl.formatMessage({ defaultMessage: "Campus Pickup Info" }),
    onClick: () => methods.viewDetails(),
    category: "details",
    type: "campus_pickup_info",
  });

  const returnInstructionsAction = (): BaseAction => ({
    key: "return_instructions",
    label: intl.formatMessage({ defaultMessage: "Return Instructions" }),
    onClick: () => methods.viewDetails(),
    category: "details",
    type: "return_instructions",
  });

  if (
    isOptedIn &&
    (orderStatus === "Preparing to Ship" || orderStatus === "Shipped")
  ) {
    actions.push(trackShipmentAction());
  } else if (isOptedIn && orderStatus === "Ready for Pickup") {
    actions.push(campusPickupInfoAction());
    actions.push(viewOrderHistoryAction());
  } else if (isOptedIn && orderStatus === "Picked Up") {
    actions.push(viewOrderHistoryAction());
  } else if (!isOptedIn && orderStatus === "Needs to be Returned") {
    actions.push(returnInstructionsAction());
  } else if (isOptedIn && !optWindowOpen) {
    actions.push(viewOrderHistoryAction());
  }

  return actions;
}
